// Tenant-Admin/Subscription/TenantSubscriptionDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  FiPackage, 
  FiClock, 
  FiCheckCircle, 
  FiAlertCircle,
  FiRefreshCw,
  FiDownload,
  FiLoader,
  FiX,
  FiArrowUpRight
} from 'react-icons/fi';
import { motion } from 'framer-motion';
import api from '../../api';
import TenantAdminLayout from '../Layout/TenantAdminLayout';
import SubscriptionPlanCard from './SubscriptionPlanCard';
import SubscriptionRenewModal from './SubscriptionRenewModal';
import SubscriptionUpgradeModal from './SubscriptionUpgradeModal';
import BillingHistory from './BillingHistory';
import PaymentCheckoutModal from './PaymentCheckoutModal';
import InvoiceList from './InvoiceList';
import { useTenant } from '../Layout/TenantContext';

const TenantSubscriptionDashboard = () => {
  const location = useLocation();
  const { tenantData } = useTenant();
  
  const tenantId = location.state?.tenantId || 
                    localStorage.getItem('tenantId') || 
                    tenantData?.tenantId;

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [, setOptions] = useState(null);
  const [billingHistory, setBillingHistory] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [invoiceSummary, setInvoiceSummary] = useState(null);
  const [showRenewModal, setShowRenewModal] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentAction] = useState(null);
  const [, setPaymentData] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  // Add a flag to track if data refresh is needed
  const [needsRefresh, setNeedsRefresh] = useState(false);

  // ============================================
  // FETCH DATA
  // ============================================

  const fetchData = useCallback(async (silent = false) => {
    const finalTenantId = tenantId || localStorage.getItem('tenantId');
    
    if (!finalTenantId) {
      console.warn('⚠️ No tenant ID available');
      setError('Your account identity could not be verified. Please contact support.');
      setLoading(false);
      return;
    }

    try {
      if (!silent) {
        setLoading(true);
      }
      setError(null);

      const token = localStorage.getItem('token');
      
      if (!token) {
        setError('Your login session has timed out. Please sign in again.');
        setLoading(false);
        return;
      }
      
      const headers = { 
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      };

      const requestBody = { tenantId: finalTenantId };

      const [subscriptionRes, plansRes, optionsRes, historyRes, invoicesRes, invoiceSummaryRes] = await Promise.all([
        api.get('/api/tenant-admin/subscription/current', { ...headers, data: requestBody }),
        api.get('/api/tenant-admin/subscription/plans', { ...headers, data: requestBody }),
        api.get('/api/tenant-admin/subscription/options', { ...headers, data: requestBody }),
        api.get('/api/tenant-admin/subscription/history', { ...headers, data: requestBody }),
        api.get('/api/tenant-admin/invoices', { ...headers, data: requestBody }),
        api.get('/api/tenant-admin/invoices/summary/stats', { ...headers, data: requestBody })
      ]);

      if (subscriptionRes.data.success) {
        setSubscription(subscriptionRes.data.data);
        localStorage.setItem('subscription', JSON.stringify(subscriptionRes.data.data));
      }

      if (plansRes.data.success) {
        setAvailablePlans(plansRes.data.data || []);
      }

      if (optionsRes.data.success) {
        setOptions(optionsRes.data.data);
      }

      if (historyRes.data.success) {
        setBillingHistory(historyRes.data.data || []);
      }

      if (invoicesRes.data.success) {
        setInvoices(invoicesRes.data.data || []);
      }

      if (invoiceSummaryRes.data.success) {
        setInvoiceSummary(invoiceSummaryRes.data.data);
      }

      setNeedsRefresh(false);

    } catch (err) {
      console.error('❌ Error fetching subscription data:', err);
      
      if (err.response?.status === 401) {
        setError('Your login session has timed out. Please sign in again.');
      } else {
        setError(err.response?.data?.message || 'Something went wrong while updating your dashboard data.');
      }
    } finally {
      if (!silent) {
        setLoading(false);
      }
      setRefreshing(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Effect to refresh data when needed (after modal closes)
  useEffect(() => {
    if (needsRefresh && !showRenewModal && !showUpgradeModal && !showPaymentModal) {
      fetchData(true); // Silent refresh
    }
  }, [needsRefresh, showRenewModal, showUpgradeModal, showPaymentModal, fetchData]);

  // ============================================
  // HANDLERS
  // ============================================

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
  };

  const handlePaymentSuccess = async (paymentData) => {
    setPaymentData(paymentData);
    setNeedsRefresh(true);
    setShowPaymentModal(false);
  };

  const handleRenew = async (data) => {
    try {
      if (data.action === 'refresh') {
        // Silent refresh - don't show loading state
        await fetchData(true);
        return { success: true };
      }

      setIsProcessing(true);
      const token = localStorage.getItem('token');
      const response = await api.post('/api/tenant-admin/subscription/renew', {
        ...data,
        tenantId: tenantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Mark that we need to refresh but don't do it immediately
        // The modal will handle showing success and then close
        setNeedsRefresh(true);
        // Don't close modal here - let the modal handle it
        return response.data;
      }
      return response.data;
    } catch (err) {
      console.error('❌ Renew failed:', err);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleUpgrade = async (data) => {
    try {
      if (data.action === 'refresh') {
        // Silent refresh - don't show loading state
        await fetchData(true);
        return { success: true };
      }

      setIsProcessing(true);
      const token = localStorage.getItem('token');
      const response = await api.post('/api/tenant-admin/subscription/upgrade', {
        ...data,
        tenantId: tenantId
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        // Mark that we need to refresh but don't do it immediately
        setNeedsRefresh(true);
        // Don't close modal here - let the modal handle it
        return response.data;
      }
      return response.data;
    } catch (err) {
      console.error('❌ Upgrade failed:', err);
      throw err;
    } finally {
      setIsProcessing(false);
    }
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceModal(true);
  };

  const handleDownloadInvoice = async (invoice) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/api/tenant-admin/invoices/${invoice._id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice-${invoice.invoiceNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('❌ Download failed:', err);
      setError('Could not download your invoice file. Please try again.');
    }
  };

  // Modal close handlers - just close the modal, don't fetch data immediately
  const handleRenewModalClose = () => {
    setShowRenewModal(false);
    // Data will be refreshed by the useEffect when modal closes
  };

  const handleUpgradeModalClose = () => {
    setShowUpgradeModal(false);
    setSelectedPlan(null);
    // Data will be refreshed by the useEffect when modal closes
  };

  const handlePaymentModalClose = () => {
    setShowPaymentModal(false);
    // Data will be refreshed by the useEffect when modal closes
  };

  // ============================================
  // LOADING STATE
  // ============================================

  if (loading) {
    return (
      <TenantAdminLayout>
        <div className="min-h-[80vh] flex flex-col items-center justify-center p-6 text-center">
          <FiLoader className="w-10 h-10 text-indigo-600 animate-spin mb-4" />
          <h3 className="text-lg font-semibold text-slate-800">Loading Account Details</h3>
          <p className="text-sm text-slate-500 mt-1 max-w-xs">We are collecting your subscription and billing data...</p>
        </div>
      </TenantAdminLayout>
    );
  }

  // ============================================
  // PARAMETER EVALUATION
  // ============================================

  const hasActivePlan = subscription?.isActive || false;
  const isExpired = subscription?.isExpired || false;
  const daysRemaining = subscription?.daysRemaining || 0;
  const isExpiringSoon = subscription?.isExpiringSoon || false;
  const status = subscription?.status || 'No Plan';

  return (
    <TenantAdminLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 md:py-10 space-y-6 md:space-y-8">
        
        {/* Top Header Block */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between border-b border-slate-100 pb-6">
          <div>
            <h1 className="text-xl md:text-2xl font-bold text-slate-900 tracking-tight">Subscription & Billing</h1>
            <p className="text-sm text-slate-500 mt-1">Review your current service tier, billing history, and official invoices.</p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 border border-slate-200 bg-white rounded-xl text-sm font-medium text-slate-700 hover:bg-slate-50 transition active:scale-98 disabled:opacity-50"
          >
            <FiRefreshCw className={`w-4 h-4 text-slate-500 ${refreshing ? 'animate-spin' : ''}`} />
            <span>{refreshing ? 'Updating...' : 'Refresh Page'}</span>
          </button>
        </div>

        {/* Global Error Notice */}
        {error && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            className="p-4 bg-red-50 border border-red-100 rounded-xl flex items-start gap-3"
          >
            <FiAlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <p className="text-sm text-red-800 font-medium">{error}</p>
              {error.includes('timed out') && (
                <button
                  onClick={() => {
                    localStorage.clear();
                    window.location.href = '/signin';
                  }}
                  className="w-full sm:w-auto text-center px-4 py-1.5 bg-red-600 hover:bg-red-700 text-white text-xs font-semibold rounded-lg transition"
                >
                  Sign In Again
                </button>
              )}
            </div>
            <button onClick={() => setError(null)} className="p-1 text-red-400 hover:text-red-600 transition">
              <FiX className="w-4 h-4" />
            </button>
          </motion.div>
        )}

        {/* Action Banners for Expiry and Expiring Soon */}
        {isExpiringSoon && hasActivePlan && !isExpired && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-4 bg-amber-50 border border-amber-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div className="flex items-start gap-3">
              <FiClock className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-bold text-amber-900">Your account plan expires in {daysRemaining} days</h4>
                <p className="text-xs text-amber-700 mt-0.5">Renew your subscription plan today to prevent any service interruptions.</p>
              </div>
            </div>
            <button
              onClick={() => setShowRenewModal(true)}
              className="w-full sm:w-auto text-center px-4 py-2 bg-amber-600 hover:bg-amber-700 text-white text-xs font-bold rounded-xl shadow-sm transition whitespace-nowrap"
            >
              Renew Account Plan
            </button>
          </motion.div>
        )}

        {isExpired && (
          <motion.div
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            className="p-4 bg-rose-50 border border-rose-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-4"
          >
            <div className="flex items-start gap-3">
              <FiAlertCircle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="text-sm font-bold text-rose-900">Your plan has expired</h4>
                <p className="text-xs text-rose-700 mt-0.5">Please purchase a subscription to restore all system features.</p>
              </div>
            </div>
            <button
              onClick={() => setShowRenewModal(true)}
              className="w-full sm:w-auto text-center px-4 py-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-bold rounded-xl shadow-sm transition whitespace-nowrap"
            >
              Renew Account Plan
            </button>
          </motion.div>
        )}

        {/* Primary Metrics Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          
          {/* Card 1: Account Tier */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col justify-between shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Account Tier</span>
                <h3 className="text-lg font-bold text-slate-900 mt-1 truncate">
                  {subscription?.currentPlan?.name || 'No Selected Plan'}
                </h3>
              </div>
              <div className={`p-2.5 rounded-xl ${hasActivePlan ? 'bg-emerald-50 text-emerald-600' : isExpired ? 'bg-rose-50 text-rose-600' : 'bg-amber-50 text-amber-600'}`}>
                {hasActivePlan ? <FiCheckCircle className="w-5 h-5" /> : <FiAlertCircle className="w-5 h-5" />}
              </div>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-50 flex items-center justify-between">
              {subscription?.currentPlan?.type && (
                <span className="text-xs font-medium px-2 py-0.5 bg-slate-100 text-slate-700 rounded-md uppercase">
                  {subscription.currentPlan.type}
                </span>
              )}
              {subscription?.actualPrice?.isDiscounted && (
                <span className="text-xs text-slate-400 line-through">₹{subscription.actualPrice.originalAmount}</span>
              )}
            </div>
          </div>

          {/* Card 2: Current Status */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col justify-between shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Status</span>
                <h3 className="text-lg font-bold text-slate-900 mt-1 capitalize">{status}</h3>
              </div>
              <span className={`text-xs font-bold px-2.5 py-1 rounded-lg ${hasActivePlan ? 'bg-emerald-50 text-emerald-700' : isExpired ? 'bg-rose-50 text-rose-700' : 'bg-amber-50 text-amber-700'}`}>
                {hasActivePlan ? 'Active' : isExpired ? 'Expired' : 'Inactive'}
              </span>
            </div>
            <p className="text-xs text-slate-500 mt-4 pt-3 border-t border-slate-50">
              {hasActivePlan && daysRemaining > 0 ? `${daysRemaining} days left until reset` : isExpired ? `Plan closed on ${new Date(subscription?.endDate).toLocaleDateString()}` : 'No system renewal cycle scheduled'}
            </p>
          </div>

          {/* Card 3: Billing Rate */}
          <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col justify-between shadow-sm">
            <div className="flex items-start justify-between gap-2">
              <div>
                <span className="text-xs font-semibold text-slate-400 uppercase tracking-wider">Billing Rate</span>
                <h3 className="text-xl font-black text-slate-900 mt-1">₹{subscription?.actualPrice?.amount || 0}</h3>
              </div>
              <span className="text-xs font-bold text-slate-500 bg-slate-100 px-2 py-1 rounded-md tracking-wider">INR</span>
            </div>
            <div className="mt-4 pt-3 border-t border-slate-50 flex items-center justify-between text-xs text-slate-500">
              <span className="capitalize">{subscription?.billingCycle ? `Billed ${subscription.billingCycle}` : 'No setup term'}</span>
              {subscription?.actualPrice?.isDiscounted && (
                <span className="text-emerald-600 font-semibold">
                  Saved {Math.round(((subscription.actualPrice.originalAmount - subscription.actualPrice.amount) / subscription.actualPrice.originalAmount) * 100)}%
                </span>
              )}
            </div>
          </div>

        </div>

        {/* Context-Sensitive System Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-3">
          {hasActivePlan && !isExpired ? (
            <>
              <button
                onClick={() => setShowRenewModal(true)}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl shadow-sm transition active:scale-98"
              >
                <FiRefreshCw className="w-4 h-4" />
                <span>Renew Subscription Plan</span>
              </button>
              <button
                onClick={() => {
                  const upgradeablePlans = availablePlans.filter(p => p.isUpgrade);
                  if (upgradeablePlans.length > 0) {
                    setSelectedPlan(upgradeablePlans[0]);
                    setShowUpgradeModal(true);
                  }
                }}
                className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-3 border border-indigo-200 bg-indigo-50 text-indigo-700 font-semibold text-sm rounded-xl hover:bg-indigo-100/70 transition active:scale-98"
              >
                <span>Upgrade Account Level</span>
                <FiArrowUpRight className="w-4 h-4" />
              </button>
            </>
          ) : (
            <button
              onClick={() => {
                const firstPlan = availablePlans.find(p => !p.isCurrent);
                if (firstPlan) {
                  setSelectedPlan(firstPlan);
                  setShowUpgradeModal(true);
                }
              }}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold text-sm rounded-xl shadow-sm transition active:scale-98"
            >
              <FiPackage className="w-4 h-4" />
              <span>Purchase Subscription Plan Now</span>
            </button>
          )}
        </div>

        {/* Dynamic Product Tier Offerings Block */}
        {availablePlans.length > 0 && (
          <div className="space-y-4">
            <h2 className="text-md font-bold text-slate-800 uppercase tracking-wide">
              {subscription?.currentPlan ? 'Explore Alternative Options' : 'Select Your Core Infrastructure Plan'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {availablePlans.map((plan) => {
                const isCurrent = plan.isCurrent || plan.planId === subscription?.currentPlan?.planId;
                const isUpgrade = plan.isUpgrade || false;
                
                if (!isCurrent && !isUpgrade) return null;
                
                return (
                  <SubscriptionPlanCard
                    key={plan.planId}
                    plan={plan}
                    isCurrent={isCurrent}
                    isUpgrade={isUpgrade}
                    onSelect={(plan) => {
                      if (isCurrent) {
                        setShowRenewModal(true);
                      } else {
                        setSelectedPlan(plan);
                        setShowUpgradeModal(true);
                      }
                    }}
                  />
                );
              })}
            </div>
          </div>
        )}

        {/* Linked Accounts & Invoice Log Modules */}
        <div className="border-t border-slate-100 pt-6">
          <InvoiceList 
            invoices={invoices}
            summary={invoiceSummary}
            onViewInvoice={handleViewInvoice}
            onDownloadInvoice={handleDownloadInvoice}
          />
        </div>

        <div className="border-t border-slate-100 pt-6">
          <BillingHistory history={billingHistory} />
        </div>

        {/* ============================================ */}
        {/* APP SUBSCRIPTION INTERACTIVE MODALS */}
        {/* ============================================ */}

        <SubscriptionRenewModal
          isOpen={showRenewModal}
          onClose={handleRenewModalClose}
          currentPlan={subscription?.currentPlan}
          currentPrice={subscription?.actualPrice?.amount || 0}
          billingCycle={subscription?.billingCycle || 'yearly'}
          currentSubscription={subscription}
          onSubmit={handleRenew}
          isSubmitting={isProcessing}
          tenantId={tenantId}
        />

        <SubscriptionUpgradeModal
          isOpen={showUpgradeModal}
          onClose={handleUpgradeModalClose}
          currentPlan={subscription?.currentPlan}
          selectedPlan={selectedPlan}
          currentPrice={subscription?.actualPrice?.amount || 0}
          currentSubscription={subscription}
          onSubmit={handleUpgrade}
          isSubmitting={isProcessing}
          tenantId={tenantId}
        />

        <PaymentCheckoutModal
          isOpen={showPaymentModal}
          onClose={handlePaymentModalClose}
          plan={selectedPlan}
          action={paymentAction}
          tenantId={tenantId}
          onSuccess={handlePaymentSuccess}
        />

        {/* Modal: View Embedded Invoice Profile */}
        {showInvoiceModal && selectedInvoice && (
          <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm z-[9999] flex items-end sm:items-center justify-center p-0 sm:p-4 animate-fadeIn">
            <div className="bg-white rounded-t-2xl sm:rounded-2xl max-w-2xl w-full max-h-[92vh] overflow-y-auto shadow-2xl flex flex-col">
              <div className="sticky top-0 bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
                <div>
                  <h2 className="text-base font-bold text-slate-900">Invoice File {selectedInvoice.invoiceNumber}</h2>
                  <p className="text-xs text-slate-400 mt-0.5">Issued on {new Date(selectedInvoice.invoiceDate).toLocaleDateString()}</p>
                </div>
                <div className="flex items-center gap-1.5">
                  <button
                    onClick={() => handleDownloadInvoice(selectedInvoice)}
                    className="p-2 text-slate-500 hover:bg-slate-100 rounded-xl transition"
                    title="Download File"
                  >
                    <FiDownload className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => {
                      setShowInvoiceModal(false);
                      setSelectedInvoice(null);
                    }}
                    className="p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-700 rounded-xl transition"
                  >
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              </div>
              
              <div className="p-6 space-y-6">
                <div className="bg-slate-50 border border-slate-100 rounded-2xl p-5 space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <span className="text-xs text-slate-400 font-medium block">Invoice Tracking ID</span>
                      <p className="text-sm font-bold text-slate-800 mt-0.5">{selectedInvoice.invoiceNumber}</p>
                    </div>
                    <div className="text-right">
                      <span className="text-xs text-slate-400 font-medium block">Total Charge Due</span>
                      <p className="text-base font-black text-slate-900 mt-0.5">₹{selectedInvoice.amount}</p>
                    </div>
                  </div>
                  
                  <div className="border-t border-slate-200/60 pt-3 flex items-center justify-between">
                    <span className="text-xs text-slate-400 font-medium">Payment Ledger Status</span>
                    <span className={`inline-flex px-2.5 py-0.5 text-xs font-bold rounded-md ${
                      selectedInvoice.status === 'paid' ? 'bg-emerald-50 text-emerald-700' :
                      selectedInvoice.status === 'pending' ? 'bg-amber-50 text-amber-700' :
                      'bg-rose-50 text-rose-700'
                    }`}>
                      {selectedInvoice.status?.toUpperCase() || 'UNKNOWN'}
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* System Network Loading Lock Screen */}
        {isProcessing && (
          <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-xs z-[10000] flex items-center justify-center p-4">
            <div className="bg-white rounded-2xl p-6 md:p-8 max-w-xs w-full shadow-2xl text-center border border-slate-100">
              <FiLoader className="w-8 h-8 text-indigo-600 animate-spin mx-auto mb-3" />
              <h4 className="text-sm font-bold text-slate-800">Processing Your Payment Request</h4>
              <p className="text-xs text-slate-400 mt-1">Please do not navigate away or reload your browser.</p>
            </div>
          </div>
        )}
      </div>
    </TenantAdminLayout>
  );
};

export default TenantSubscriptionDashboard;